import { useEffect, useState } from 'react';
import { publicAttendanceLocationSchema, PUBLIC_ATTENDANCE_LOCATION_MAX_AGE_MS } from 'shared-models/browser';
import type { PublicAttendanceLocation } from 'shared-models/browser';

const locationMessage = 'יש להפעיל מיקום ולאפשר גישה למיקום כדי להיכנס למשמרת';
const options: PositionOptions = { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 };

function readPosition(position: GeolocationPosition): PublicAttendanceLocation {
  const parsed = publicAttendanceLocationSchema.safeParse({ latitude: position.coords.latitude, longitude: position.coords.longitude,
    accuracy: position.coords.accuracy, timestamp: position.timestamp });
  if (!parsed.success || Math.abs(Date.now() - position.timestamp) > PUBLIC_ATTENDANCE_LOCATION_MAX_AGE_MS) throw new Error(locationMessage);
  return parsed.data;
}

export function usePublicAttendanceLocation() {
  const [location, setLocation] = useState<PublicAttendanceLocation | null>(null);
  const [locationError, setLocationError] = useState('');
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    let expiry: ReturnType<typeof setTimeout>;
    let permission: PermissionStatus | undefined;
    setLocation(null); setLocationError('');
    const unavailable = () => { if (active) { clearTimeout(expiry); setLocation(null); setLocationError(locationMessage); } };
    if (!navigator.geolocation) { unavailable(); return; }
    const watcher = navigator.geolocation.watchPosition((position) => {
      if (!active) return;
      try {
        const fresh = readPosition(position);
        clearTimeout(expiry); setLocation(fresh); setLocationError('');
        expiry = setTimeout(unavailable, Math.max(0, fresh.timestamp + PUBLIC_ATTENDANCE_LOCATION_MAX_AGE_MS - Date.now()));
      } catch { unavailable(); }
    }, unavailable, options);
    void navigator.permissions?.query({ name: 'geolocation' }).then((status) => {
      if (!active) return;
      permission = status;
      status.onchange = () => { if (status.state === 'denied') unavailable(); };
      if (status.state === 'denied') unavailable();
    }).catch(() => undefined);
    return () => { active = false; clearTimeout(expiry); navigator.geolocation.clearWatch(watcher); if (permission) permission.onchange = null; };
  }, [attempt]);

  const currentLocation = () => new Promise<PublicAttendanceLocation>((resolve, reject) => {
    if (!navigator.geolocation) { reject(new Error(locationMessage)); return; }
    navigator.geolocation.getCurrentPosition((position) => {
      try { resolve(readPosition(position)); } catch (error) { reject(error); }
    }, () => { setLocation(null); setLocationError(locationMessage); reject(new Error(locationMessage)); }, options);
  });
  return { location, locationError, currentLocation, retryLocation: () => setAttempt((previous) => previous + 1) };
}